import { ChatBotConversationProps } from "@/components/lazo-ai-chat-components/ChatBotConversationProps";
import { AppBarContentProvider } from "@/contexts/AppBarContentContext";
import { SelectedChartProvider } from "@/contexts/ChartsContext";
import { CountriesProvider } from "@/contexts/CountriesContext";
import { LazoView, useLazoView } from "@/contexts/CurrentLazoViewContext";
import { FeatureTourProvider } from "@/contexts/FeatureTourContext";
import { LazoAIChatProvider } from "@/contexts/FilterConversationContext";
import { FundraisingProvider } from "@/contexts/FunderContext";
import { useLanguageContext } from "@/contexts/LanguageContext";
import { NavbarBackgroundProvider } from "@/contexts/NavbarBackgroundContext";
import { NotificationsContextProvider } from "@/contexts/NotificationsContext";
import { RightDrawerProvider } from "@/contexts/RightDrawerContext";
import { SelectedConversationProvider } from "@/contexts/SelectedConversationContext";
import { useSubscriptionPurchase } from "@/contexts/SubscriptionPurchaseContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths, LazoPathsWithChat } from "@/enums/LazoPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { useToggle } from "@/hooks/useToggle";
import { SubscriptionPurchase } from "@/models/subscriptions/SubscriptionPurchase";
import MergeIntegrationService from "@/services/MergeIntegrationService";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { LazoAppContainer } from "./LazoAppContainer";
import DrawerHeaderSection from "./drawer-components/DrawerHeaderSection";
import { LazoDrawerFooter } from "./drawer-components/LazoDrawerFooter";
import ConnectAccountingModal from "./integrations/ConnectAccountingModal";
import { getItems, getPortalItems } from "./layout/sidebarItems";
import LazoAIChatRightContainerWrapper from "./lazo-ai-chat-components/LazoAIChatRightContainerWrapper";
import { Navbar } from "./navigation/Navbar";
import { ModuleNoAccessModal } from "./paywall/ModuleNoAccessModal";
import { FullscreenLoading } from "./utils/FullscreenLoading";

const LazoPathsToFilter = [
  LazoPaths.DASHBOARD,
  LazoPaths.FUNDRAISING,
  LazoPaths.FUNDER_LIST_DETAIL.replace(":externalId", ""),
  LazoPaths.FUNDRAISING_ADVANCED_SEARCH.replace(":externalId", ""),
  LazoPaths.ONE_DATA_ROOM,
  LazoPaths.ONE_VIRTUAL_DATA_ROOMS_INSIDE,
  LazoPaths.VIRTUAL_DATA_ROOM_FINANCIAL_REPORT,
  LazoPaths.ONE_VIRTUAL_DATA_ROOMS_FILE,
];

const isDataRoomPath = (pathname: string): boolean =>
  pathname.startsWith(LazoPaths.ONE_DATA_ROOM) || pathname.startsWith(LazoPaths.ONE_VIRTUAL_DATA_ROOMS);

function persistentLayotEqual(oldProps: PersistentLayoutProps, newProps: PersistentLayoutProps) {
  return (
    oldProps.loadingComponent === newProps.loadingComponent &&
    oldProps.drawerItems === newProps.drawerItems &&
    // oldProps.breadcrumbs === newProps.breadcrumbs &&
    oldProps.lazoUser === newProps.lazoUser
  );
}

interface PersistentLayoutProps {
  loadingComponent: null | JSX.Element | JSX.Element[];
  drawerItems: any[];
  renderDefaultAppBar: boolean;
  lazoView: LazoView;
  lazoUser: any;
  footer: React.ReactNode | ((open: boolean) => React.ReactNode);
}

const PersistentLayout = React.memo(function PersistentLayout({
  loadingComponent,
  drawerItems,
  renderDefaultAppBar,
  lazoView,
  lazoUser,
  footer,
}: PersistentLayoutProps) {
  // Lifted state with persistence
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem("lazo_sidebar_open");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggleOpen = () => {
    setOpen((prev: boolean) => {
      const newState = !prev;
      localStorage.setItem("lazo_sidebar_open", JSON.stringify(newState));
      return newState;
    });
  };

  const [mobileOpen, toggleMobileOpen] = useToggle(false);

  // Drawer Header Logic
  const drawerHeader = lazoView === LazoView.LAZO_ONE ? (drawerOpen: boolean) => <DrawerHeaderSection open={drawerOpen} /> : null;

  return (
    <LazoAppContainer
      open={open}
      onToggleOpen={toggleOpen}
      mobileOpen={mobileOpen}
      onToggleMobileOpen={toggleMobileOpen}
      drawerHeader={drawerHeader}
      navbar={<Navbar />}
      hideTopSpacer={false}
      containerBackgroundColor={Colors.BLACK}
      clippedRightContainer={false}
      clippedDrawer={false}
      drawerWidth={220}
      rightContainer={
        <LazoAIChatProvider>
          <LazoAIChatRightContainerWrapper lazoUser={lazoUser} />
        </LazoAIChatProvider>
      }
      renderDefaultAppBar={renderDefaultAppBar}
      drawerItems={drawerItems}
      footer={footer}
    >
      <Suspense fallback={<Spinner sx={{ color: Colors.LAZO_PURPLE }} />}>{loadingComponent ? loadingComponent : <Outlet />}</Suspense>
    </LazoAppContainer>
  );
}, persistentLayotEqual);

export function AppContainerWrapper(componentProps: ChatBotConversationProps) {
  const location = useLocation();
  const { language } = useLanguageContext();
  const { lazoView } = useLazoView();

  const [renderDefaultAppBar, setRenderDefaultAppBar] = useState(false);
  const [openConnectIntegrationsModal, setOpenConnectIntegrationsModal] = useState(false);

  const subscriptionPurchaseData = useSubscriptionPurchase();

  const { currentCompany, currentTenantMembership, workspaceState } = useTenantMemberships();

  useEffect(() => {
    if (!location.pathname.includes(LazoPaths.DASHBOARD)) {
      setOpenConnectIntegrationsModal(false);
      return;
    }

    if (currentTenantMembership.tenant) {
      MergeIntegrationService.getIntegrations(currentTenantMembership.tenant.externalId)
        .then((r) => {
          const integrations = r.getContentOrThrowError();
          setOpenConnectIntegrationsModal(integrations.length <= 0);
          const mergeIntegration = integrations.find((integration) => integration.type === "MERGE");
          if (integrations.length > 0 && mergeIntegration) {
            MergeIntegrationService.mergeIntegrationHasFinished(mergeIntegration.externalId).then((i) => {
              const result = i.getContentOrThrowError();
              if (!result) {
                setOpenConnectIntegrationsModal(true);
              }
            });
          }
        })
        .catch((e: { message: string }) => console.log(e.message));
    }
  }, [location.pathname, currentTenantMembership.tenant]);

  useEffect(() => {
    if (lazoView !== LazoView.LAZO_PORTAL) {
      const shouldRender = LazoPathsWithChat.filter((path) => !LazoPathsToFilter.includes(path)).some((path) =>
        location.pathname.includes(path)
      );
      setRenderDefaultAppBar(shouldRender);
    } else {
      setRenderDefaultAppBar(false);
    }
  }, [location.pathname, lazoView]);

  const drawerItems = useMemo(() => {
    if (lazoView === LazoView.LAZO_PORTAL) {
      return getPortalItems();
    }
    return getItems(
      currentCompany?.lazoCompanyAccountType,
      lazoView === LazoView.LAZO_ONE
        ? subscriptionPurchaseData?.subscriptionPurchase
        : ({
            subscription: { addOns: [] },
            extraAddOns: [],
          } as SubscriptionPurchase),
      !!currentCompany?.bookkeepingClient,
      currentCompany?.legalName
    );
    // `language` is read indirectly via `i18n.t(...)` inside getItems, so it must trigger a rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCompany?.lazoCompanyAccountType,
    currentCompany?.bookkeepingClient,
    currentCompany?.legalName,
    subscriptionPurchaseData?.subscriptionPurchase,
    lazoView,
    language,
  ]);

  // Get subscription and access info (only for LAZO_ONE)
  const { loadingSubscription, accessMap, subscriptionPurchase } =
    lazoView === LazoView.LAZO_ONE
      ? subscriptionPurchaseData
      : {
          loadingSubscription: false,
          accessMap: {},
          subscriptionPurchase: null,
        };
  const isExternalUser = !workspaceState?.isLazoAdminOrKam;
  const shouldSkipDataRoomPaywall = isExternalUser && isDataRoomPath(location.pathname);
  const shouldRenderNoAccessModal = accessMap[location.pathname] === false && !shouldSkipDataRoomPaywall;

  // Only show the fullscreen overlay on the very first subscription load.
  // For subsequent refetches (e.g. tenant switches) we already have cached data, so we let the
  // page re-render silently instead of flashing a black overlay.
  const isInitialSubscriptionLoad = loadingSubscription && !subscriptionPurchase;

  const loadingComponent = isInitialSubscriptionLoad ? (
    <FullscreenLoading />
  ) : shouldRenderNoAccessModal ? (
    <ModuleNoAccessModal />
  ) : openConnectIntegrationsModal ? (
    <ConnectAccountingModal open={openConnectIntegrationsModal} isFetching={false} />
  ) : null;

  return (
    <SelectedConversationProvider>
      <NotificationsContextProvider>
        <NavbarBackgroundProvider>
          <AppBarContentProvider>
            <RightDrawerProvider>
              <SelectedChartProvider>
                <FundraisingProvider>
                  <CountriesProvider>
                    <FeatureTourProvider>
                      <PersistentLayout
                        loadingComponent={loadingComponent}
                        drawerItems={drawerItems}
                        renderDefaultAppBar={renderDefaultAppBar}
                        lazoView={lazoView}
                        lazoUser={componentProps.lazoUser}
                        footer={(open: boolean) => <LazoDrawerFooter open={open} lazoUser={componentProps.lazoUser} />}
                      />
                    </FeatureTourProvider>
                  </CountriesProvider>
                </FundraisingProvider>
              </SelectedChartProvider>
            </RightDrawerProvider>
          </AppBarContentProvider>
        </NavbarBackgroundProvider>
      </NotificationsContextProvider>
    </SelectedConversationProvider>
  );
}
